import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  Button,
  Sheet,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Chip
} from '@mui/joy';
import { CaretDown as ChevronDown, Check } from '@phosphor-icons/react';
import { useBoardsWithStatuses } from '../hooks/useJobs';
import type { BoardWithStatuses } from '../types';

interface GroupedStatusSelectProps {
  currentStatusId?: number;
  currentStatusName?: string;
  onStatusChange: (statusId: number) => void;
  disabled?: boolean;
}

export const GroupedStatusSelect: React.FC<GroupedStatusSelectProps> = ({
  currentStatusId,
  currentStatusName,
  onStatusChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const { data: boardsData, isLoading, error } = useBoardsWithStatuses();
  
  // Debug logging
  console.log('GroupedStatusSelect - boardsData:', boardsData);
  console.log('GroupedStatusSelect - isLoading:', isLoading);
  console.log('GroupedStatusSelect - error:', error);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inButton = dropdownRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inButton && !inMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  // Position the portaled menu under the trigger. It's portaled to <body> so it
  // escapes the glass cards' backdrop-filter stacking contexts (which were
  // painting sibling cards on top of it). Re-measure on scroll/resize.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen]);

  const handleStatusSelect = (statusId: number) => {
    onStatusChange(statusId);
    setIsOpen(false);
  };

  const getCurrentStatusDisplay = () => {
    if (currentStatusName) {
      return currentStatusName;
    }
    
    if (currentStatusId && boardsData?.boards && Array.isArray(boardsData.boards)) {
      for (const board of boardsData.boards) {
        if (board.statuses && Array.isArray(board.statuses)) {
          const status = board.statuses.find((s: BoardWithStatuses['statuses'][number]) => s.id === currentStatusId);
          if (status) {
            return status.name;
          }
        }
      }
    }
    
    return 'Select Status';
  };

  const renderStatusDropdown = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      );
    }

    if (error || !boardsData || !boardsData.boards || !Array.isArray(boardsData.boards)) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography level="body-sm" color="danger">
            Failed to load statuses
          </Typography>
        </Box>
      );
    }

    const boardsArray = Array.isArray(boardsData?.boards) ? boardsData.boards : [];
    
    return (
      <List sx={{ p: 0 }}>
        {boardsArray.map((board: BoardWithStatuses) => (
          <Box key={board.id}>
            {/* Board Header - Non-selectable */}
            <ListItem sx={{ p: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  px: 2,
                  py: 1.5,
                  backgroundColor: 'background.level1',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography
                  level="title-sm"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    cursor: 'default'
                  }}
                >
                  {board.name}
                </Typography>
              </Box>
            </ListItem>
            
            {/* Status Options */}
            {Array.isArray(board.statuses) && board.statuses.map((status) => (
              <ListItem key={status.id} sx={{ p: 0 }}>
                <ListItemButton
                  onClick={() => handleStatusSelect(status.id)}
                  sx={{
                    pl: 3,
                    pr: 2,
                    py: 1,
                    minHeight: 40,
                    '&:hover': {
                      backgroundColor: 'background.level2',
                    },
                    backgroundColor: currentStatusId === status.id ? 'primary.softBg' : 'transparent'
                  }}
                  role="option"
                  aria-selected={currentStatusId === status.id}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography level="body-sm" sx={{ flex: 1 }}>
                      -- {status.name}
                    </Typography>
                    {currentStatusId === status.id && (
                      <Check size={16} color="var(--joy-palette-primary-main)" />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </Box>
        ))}
      </List>
    );
  };

  return (
    <Box sx={{ position: 'relative' }} ref={dropdownRef}>
      <Button
        ref={buttonRef}
        variant="outlined"
        color="primary"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        endDecorator={<ChevronDown size={16} />}
        sx={{
          justifyContent: 'space-between',
          minWidth: 200,
          textAlign: 'left'
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select job status"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="body-sm" sx={{ textAlign: 'left' }}>
            {getCurrentStatusDisplay()}
          </Typography>
          {currentStatusId && (
            <Chip variant="soft" size="sm" color="primary">
              Current
            </Chip>
          )}
        </Box>
      </Button>

      {isOpen && menuPos && createPortal(
        <Sheet
          ref={menuRef}
          variant="outlined"
          sx={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: 300,
            overflowY: 'auto',
            zIndex: 1300,
            borderRadius: 'sm',
            boxShadow: 'lg'
          }}
          role="listbox"
          aria-label="Status options grouped by board"
        >
          {renderStatusDropdown()}
        </Sheet>,
        document.body
      )}
    </Box>
  );
};